import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceArea, ReferenceLine, CartesianGrid, Legend } from "recharts";
import { Plus, Activity, Info } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const METRIC_META = {
  hba1c:     { label: "HbA1c",      unit: "%",     color: "#c45f4a", stroke: "#c45f4a" },
  ldl:       { label: "LDL",        unit: "mg/dL", color: "#dc2626", stroke: "#dc2626" },
  bp_sys:    { label: "BP (sys)",   unit: "mmHg",  color: "#2563eb", stroke: "#2563eb" },
  bp_dia:    { label: "BP (dia)",   unit: "mmHg",  color: "#60a5fa", stroke: "#60a5fa" },
  bmi:       { label: "BMI",        unit: "",      color: "#16a34a", stroke: "#16a34a" },
  weight_kg: { label: "Weight",     unit: "kg",    color: "#7c3aed", stroke: "#7c3aed" },
};

const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

/** Custom tooltip that shows the raw value + the AI "Lab Trend Narration"
 *  for that specific log entry. Narration only appears when the backend
 *  generated one (i.e. this log had deltas against the prior log). */
function BiomarkerTooltip({ active, payload, metricMeta }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const val = row[payload[0].dataKey];
  return (
    <div className="rounded-md border border-border bg-white shadow-md p-3 max-w-xs" data-testid="biomarker-tooltip">
      <div className="text-[11px] text-muted-foreground">{new Date(row.at).toLocaleString()}</div>
      <div className="mt-1 text-sm font-medium text-primary">
        {metricMeta.label}: <span style={{ color: metricMeta.color }}>{val}{metricMeta.unit ? ` ${metricMeta.unit}` : ""}</span>
      </div>
      {row.narration && (
        <div className="mt-2 pt-2 border-t border-border" data-testid="biomarker-tooltip-narration">
          <div className="text-[10px] uppercase tracking-widest text-primary flex items-center gap-1">
            ✨ Trend narration
          </div>
          <div className="mt-1 text-xs text-primary whitespace-pre-line leading-relaxed">{row.narration}</div>
        </div>
      )}
      <div className="mt-2 text-[10px] text-muted-foreground italic">Click the dot to refresh narration</div>
    </div>
  );
}

export default function BiomarkerTracker() {
  const [state, setState] = useState({ logs: [], opportunities: [] });
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ hba1c: "", ldl: "", bp_sys: "", bp_dia: "", bmi: "", weight_kg: "", note: "" });
  const [metric, setMetric] = useState("hba1c");
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/genome/my/biomarkers");
      setState(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const chartData = useMemo(() => {
    return state.logs.map((l) => ({
      id: l.id,
      t: new Date(l.at).getTime(),
      at: l.at,
      narration: l.narration,
      ...l.values,
    }));
  }, [state.logs]);

  const [replay, setReplay] = useState(null); // { logId, at, narration, loading }

  const openReplay = (point) => {
    if (!point?.id) return;
    setReplay({ logId: point.id, at: point.at, narration: point.narration, loading: false });
  };

  const regenNarration = async () => {
    if (!replay?.logId) return;
    setReplay((p) => ({ ...p, loading: true }));
    try {
      const { data } = await api.post(`/genome/my/biomarkers/${replay.logId}/narrate`);
      setReplay((p) => ({ ...p, narration: data.narration, loading: false }));
      toast.success("Fresh narration generated");
      load();  // refresh underlying data
    } catch (e) {
      setReplay((p) => ({ ...p, loading: false }));
      toast.error(formatApiError(e));
    }
  };

  // Opportunity-start markers — ALL started/completed opportunities, not just
  // the ones targeting the current metric. Patients see when each habit
  // change began, even for metrics the opportunity doesn't directly target.
  const allOppStarts = useMemo(() => {
    return (state.opportunities || [])
      .filter((o) => o.started_at)
      .map((o) => ({
        key: o.id,
        label: o.title,
        domain: o.domain,
        start: new Date(o.started_at).getTime(),
        targets: (o.metrics || []).includes(metric),
      }));
  }, [state.opportunities, metric]);

  // Opportunities whose metrics include the currently selected metric → shaded bands.
  const activeBands = useMemo(() => {
    const now = Date.now();
    return (state.opportunities || [])
      .filter((o) => o.metrics?.includes(metric) && o.started_at)
      .map((o) => ({
        key: o.id,
        label: o.title,
        start: new Date(o.started_at).getTime(),
        end: now,
      }));
  }, [state.opportunities, metric]);

  const submitLog = async () => {
    const payload = { note: form.note.trim() || undefined };
    for (const k of Object.keys(METRIC_META)) {
      const v = form[k];
      if (v !== "" && v != null && !Number.isNaN(Number(v))) payload[k] = Number(v);
    }
    const hasAny = Object.keys(payload).some((k) => k !== "note");
    if (!hasAny) { toast.error("Enter at least one value"); return; }
    setPosting(true);
    try {
      const { data } = await api.post("/genome/my/biomarkers", payload);
      toast.success("Biomarker log saved");
      if (data?.narration) {
        toast(data.narration, { duration: 8000, icon: "✨", description: "Trend narration — AI-generated from your changes" });
      }
      setForm({ hba1c: "", ldl: "", bp_sys: "", bp_dia: "", bmi: "", weight_kg: "", note: "" });
      setAddOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPosting(false); }
  };

  const latest = useMemo(() => {
    for (let i = state.logs.length - 1; i >= 0; i -= 1) {
      if (state.logs[i].values?.[metric] != null) return state.logs[i].values[metric];
    }
    return null;
  }, [state.logs, metric]);

  const meta = METRIC_META[metric];
  const twoLogsForMetric = chartData.filter((d) => d[metric] != null).length >= 2;

  return (
    <div className="rounded-lg border border-border bg-white p-5" data-testid="genome-biomarkers-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Outcome Attribution · V1</div>
          <div className="mt-1 font-serif text-xl text-primary">Did your interventions move the number?</div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger className="h-9 w-44" data-testid="biomarker-metric"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(METRIC_META).map(([k, m]) => (
                <SelectItem key={k} value={k}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={() => setAddOpen(true)} data-testid="biomarker-add-open">
            <Plus size={14} className="mr-1" /> Log values
          </Button>
        </div>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-5">
        <div className="md:col-span-1 space-y-2 text-sm">
          <div className="rounded-md border border-border p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Latest {meta.label}</div>
            <div className="mt-1 font-serif text-2xl text-primary" style={{ color: meta.color }}>
              {latest != null ? `${latest}${meta.unit ? ` ${meta.unit}` : ""}` : "—"}
            </div>
          </div>
          <div className="rounded-md border border-border p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Attributing to</div>
            {activeBands.length ? (
              <ul className="mt-1 space-y-1">
                {activeBands.map((b) => (
                  <li key={b.key} className="text-xs text-primary flex items-start gap-1.5">
                    <Activity size={10} className="mt-1 shrink-0" /> {b.label}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mt-1 text-xs text-muted-foreground">No active opportunity targets this metric yet.</div>
            )}
          </div>
        </div>

        <div className="md:col-span-4 h-64" data-testid="biomarker-chart">
          {loading ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Loading…</div>
          ) : !twoLogsForMetric ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-sm text-muted-foreground gap-1">
              <Info size={16} className="text-primary" />
              <div>Log at least 2 values of <b>{meta.label}</b> to see a trend.</div>
              <div className="text-xs">Values logged during an active opportunity shade the chart — so you can see what moved the number.</div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
                <XAxis
                  dataKey="t"
                  type="number"
                  domain={[
                    (dataMin) => {
                      const earliestOp = allOppStarts.length ? Math.min(...allOppStarts.map(o => o.start)) : dataMin;
                      return Math.min(dataMin, earliestOp);
                    },
                    "dataMax",
                  ]}
                  tickFormatter={(v) => fmtDate(v)}
                  stroke="#6b7280"
                  fontSize={11}
                />
                <YAxis stroke="#6b7280" fontSize={11} domain={["auto", "auto"]} />
                <Tooltip content={<BiomarkerTooltip metricMeta={meta} />} />
                <Legend verticalAlign="top" height={24} wrapperStyle={{ fontSize: 11 }} />
                {activeBands.map((b) => (
                  <ReferenceArea
                    key={b.key}
                    x1={b.start}
                    x2={b.end}
                    fill={meta.color}
                    fillOpacity={0.08}
                    stroke={meta.color}
                    strokeOpacity={0.3}
                    label={{ value: b.label, position: "insideTopLeft", fill: meta.color, fontSize: 10 }}
                  />
                ))}
                {activeBands.map((b) => (
                  <ReferenceLine key={`l-${b.key}`} x={b.start} stroke={meta.color} strokeDasharray="4 2" strokeOpacity={0.6} />
                ))}
                {/* Opportunity-start markers for all other started opportunities */}
                {allOppStarts.filter(o => !o.targets).map((o) => (
                  <ReferenceLine
                    key={`os-${o.key}`}
                    x={o.start}
                    stroke="#6b7280"
                    strokeDasharray="2 3"
                    strokeOpacity={0.5}
                    label={{ value: `▸ ${o.label}`, position: "insideTop", fill: "#6b7280",
                             fontSize: 9, offset: 4 }}
                    data-testid={`opp-start-marker-${o.key}`}
                  />
                ))}
                <Line
                  type="monotone"
                  dataKey={metric}
                  name={meta.label}
                  stroke={meta.stroke}
                  strokeWidth={2.4}
                  dot={{ r: 4, fill: meta.color, cursor: "pointer" }}
                  activeDot={{ r: 6, cursor: "pointer", onClick: (_e, p) => openReplay(p?.payload) }}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Visible, testable list of opportunity-start markers (companion to
          the Recharts ReferenceLines, which don't forward data-testid). */}
      {allOppStarts.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="opp-start-marker-strip">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-1">Started opportunities</span>
          {allOppStarts.map((o) => (
            <span
              key={`strip-${o.key}`}
              data-testid={`opp-start-marker-${o.key}`}
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] ${
                o.targets ? "border-primary/40 bg-primary/5 text-primary" : "border-border bg-muted/40 text-muted-foreground"
              }`}
              title={`${o.label} started ${fmtDate(o.start)}`}
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: o.targets ? meta.color : "#6b7280" }} />
              {o.label}
              <span className="opacity-60">· {fmtDate(o.start)}</span>
            </span>
          ))}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="biomarker-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-primary text-xl">Log biomarker values</DialogTitle>
            <DialogDescription>Enter the latest numbers from your lab or home device. Leave blank what you don't have.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(METRIC_META).map(([k, m]) => (
              <div key={k}>
                <Label>{m.label}{m.unit ? ` (${m.unit})` : ""}</Label>
                <Input
                  type="number" step="0.1" value={form[k]}
                  onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))}
                  data-testid={`biomarker-input-${k}`}
                />
              </div>
            ))}
          </div>
          <div>
            <Label>Context (optional)</Label>
            <Input
              value={form.note}
              onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
              placeholder="Fasted, post-breakfast, home monitor…"
              data-testid="biomarker-note"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={submitLog} disabled={posting} data-testid="biomarker-submit">
              {posting ? "Saving…" : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!replay} onOpenChange={(o) => { if (!o) setReplay(null); }}>
        <DialogContent data-testid="biomarker-replay-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-primary text-xl">Narration replay</DialogTitle>
            <DialogDescription>
              {replay?.at ? new Date(replay.at).toLocaleString() : ""} — regenerate the trend narration with your <em>current</em> lifestyle and active opportunities.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-primary/15 bg-primary/5 p-3 min-h-[80px]">
            {replay?.loading ? (
              <div className="text-sm text-muted-foreground">Re-reading your context…</div>
            ) : replay?.narration ? (
              <div className="text-sm text-primary whitespace-pre-line leading-relaxed" data-testid="biomarker-replay-narration">
                {replay.narration}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">No narration yet for this log — tap Regenerate to create one.</div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setReplay(null)}>Close</Button>
            <Button onClick={regenNarration} disabled={replay?.loading} data-testid="biomarker-replay-regen">
              {replay?.loading ? "Generating…" : "Regenerate"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
